import {
	type AlertRule,
	type AlertRuleKey,
	type AlertRules,
	ApiError,
	type DigestPreview,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../primitives/Button";
import { Field } from "../primitives/Field";
import { Input, NumberInput } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Switch } from "../primitives/Selection";
import { SkeletonCard } from "../primitives/Skeleton";
import { ErrorState } from "./page";

/**
 * Store alert settings (EP12-S14, DA-12-11): the daily digest's time and zone,
 * the stuck-order SLA, the low-stock real-time trickle and — for the
 * organisation — the low-stock hysteresis, each with its effective value and
 * where it comes from. The organisation's defaults on Settings → Channels, a
 * store's own in the Store Portal.
 *
 * The switch saves on its own; the other fields save together. Each changed
 * field is one `PUT` (the server's unit is a rule); "Reset" removes this
 * scope's override so it inherits again.
 */

/** The fields a form edits as text, in screen order. */
const TEXT_KEYS = [
	"digest_local_time",
	"digest_timezone",
	"order_stuck_sla_hours",
	"low_stock_hysteresis_pct",
] as const satisfies readonly AlertRuleKey[];
type TextKey = (typeof TEXT_KEYS)[number];

/** A rule's value as the form shows it. */
export function textOf(rule: AlertRule | undefined): string {
	if (rule === undefined) {
		return "";
	}
	const value: unknown = JSON.parse(rule.valueJson);
	return String(value);
}

/** A form value as the rule's JSON text: numbers for the counts, strings otherwise. */
export function jsonOf(key: TextKey, text: string): string {
	const trimmed = text.trim();
	if (key === "order_stuck_sla_hours" || key === "low_stock_hysteresis_pct") {
		return /^-?\d+$/.test(trimmed) ? trimmed : JSON.stringify(trimmed);
	}
	return JSON.stringify(trimmed);
}

function zones(current: string): { value: string; label: string }[] {
	let all: string[] = [];
	try {
		all = Intl.supportedValuesOf("timeZone");
	} catch {
		all = [];
	}
	if (current !== "" && !all.includes(current)) {
		all = [current, ...all];
	}
	return all.map((z) => ({ value: z, label: z }));
}

export interface AlertSettingsCardProps {
	t: Translate;
	title: string;
	subtitle?: string;
	/** The cache key this scope's rules sit under. */
	queryKey: readonly unknown[];
	load: (signal: AbortSignal) => Promise<AlertRules>;
	save: (key: AlertRuleKey, valueJson: string | null) => Promise<AlertRules>;
	/** A store's digest preview; absent for the organisation. */
	preview?: () => Promise<DigestPreview>;
	editable: boolean;
}

export function AlertSettingsCard({
	t,
	title,
	subtitle,
	queryKey,
	load,
	save,
	preview,
	editable,
}: AlertSettingsCardProps) {
	const view = useQuery({ queryKey, queryFn: ({ signal }) => load(signal) });
	return (
		<section aria-label={title} className="flex flex-col gap-3 card p-4">
			<div>
				<h2 className="text-[14px] font-bold text-fg">{title}</h2>
				{subtitle !== undefined && <p className="text-[12px] text-fg-subtle">{subtitle}</p>}
			</div>
			{view.isPending ? (
				<SkeletonCard />
			) : view.isError ? (
				<ErrorState t={t} error={view.error} compact onRetry={() => void view.refetch()} />
			) : (
				<AlertSettingsForm
					// A saved answer resets the drafts to what the server holds.
					key={view.data.rules.map((r) => r.valueJson + r.source).join("|")}
					t={t}
					view={view.data}
					queryKey={queryKey}
					save={save}
					editable={editable}
				/>
			)}
			{preview !== undefined && <DigestPreviewPanel t={t} preview={preview} />}
		</section>
	);
}

function AlertSettingsForm({
	t,
	view,
	queryKey,
	save,
	editable,
}: {
	t: Translate;
	view: AlertRules;
	queryKey: readonly unknown[];
	save: AlertSettingsCardProps["save"];
	editable: boolean;
}) {
	const queries = useQueryClient();
	const own = view.scopeRef.startsWith("store:") ? "store" : "org";
	const rule = (key: AlertRuleKey) => view.rules.find((r) => r.ruleKey === key);
	const [drafts, setDrafts] = useState<Record<TextKey, string>>(() => {
		const out = {} as Record<TextKey, string>;
		for (const key of TEXT_KEYS) {
			out[key] = textOf(rule(key));
		}
		return out;
	});
	const [failed, setFailed] = useState<AlertRuleKey | null>(null);
	const mutation = useMutation({
		mutationFn: async (changes: [AlertRuleKey, string | null][]) => {
			let last: AlertRules | undefined;
			for (const [key, value] of changes) {
				setFailed(key);
				last = await save(key, value);
				queries.setQueryData(queryKey, last);
			}
			setFailed(null);
			return last;
		},
	});
	const busy = mutation.isPending;
	const refusal = mutation.error instanceof ApiError ? mutation.error : null;
	const errorOf = (key: AlertRuleKey) =>
		refusal !== null && failed === key ? refusal.message : undefined;

	const caption = (key: AlertRuleKey) => {
		const r = rule(key);
		if (r === undefined) {
			return null;
		}
		return (
			<span className="flex flex-wrap items-center gap-2 text-[12px] text-fg-subtle">
				{t(`alerts.source.${r.source}`)}
				{editable && r.settable && r.source === own && (
					<button
						type="button"
						className="text-accent underline-offset-2 hover:underline"
						disabled={busy}
						onClick={() => mutation.mutate([[key, null]])}
					>
						{t("alerts.reset")}
					</button>
				)}
			</span>
		);
	};
	const changed = TEXT_KEYS.filter(
		(key) => rule(key)?.settable === true && drafts[key].trim() !== textOf(rule(key)),
	);
	const disabled = (key: AlertRuleKey) => !editable || busy || rule(key)?.settable !== true;
	const set = (key: TextKey) => (value: string) => setDrafts((d) => ({ ...d, [key]: value }));
	const trickle = rule("low_stock_realtime_trickle");

	return (
		<form
			className="flex flex-col gap-4"
			aria-busy={busy || undefined}
			onSubmit={(event) => {
				event.preventDefault();
				mutation.mutate(changed.map((key) => [key, jsonOf(key, drafts[key])]));
			}}
		>
			<div className="flex flex-wrap items-start gap-3">
				<div className="flex flex-col gap-1">
					<Field label={t("alerts.digestTime")} error={errorOf("digest_local_time")}>
						{(control) => (
							<Input
								{...control}
								type="time"
								dir="ltr"
								disabled={disabled("digest_local_time")}
								value={drafts.digest_local_time}
								onChange={(event) => set("digest_local_time")(event.target.value)}
							/>
						)}
					</Field>
					{caption("digest_local_time")}
				</div>
				<div className="flex flex-col gap-1">
					<Field label={t("alerts.digestTimezone")} error={errorOf("digest_timezone")}>
						{(control) => (
							<Select
								{...control}
								options={zones(drafts.digest_timezone)}
								value={drafts.digest_timezone}
								disabled={disabled("digest_timezone")}
								onValueChange={set("digest_timezone")}
							/>
						)}
					</Field>
					{caption("digest_timezone")}
				</div>
			</div>
			<div className="flex flex-col gap-1">
				<Field
					label={t("alerts.stuckHours")}
					hint={t("alerts.stuckHoursHint")}
					error={errorOf("order_stuck_sla_hours")}
				>
					{(control) => (
						<NumberInput
							{...control}
							integerOnly
							unit={t("alerts.hours")}
							className="max-w-40"
							disabled={disabled("order_stuck_sla_hours")}
							value={drafts.order_stuck_sla_hours}
							onChange={(event) => set("order_stuck_sla_hours")(event.target.value)}
						/>
					)}
				</Field>
				{caption("order_stuck_sla_hours")}
			</div>
			{rule("low_stock_hysteresis_pct")?.settable === true && (
				<div className="flex flex-col gap-1">
					<Field
						label={t("alerts.hysteresis")}
						hint={t("alerts.hysteresisHint")}
						error={errorOf("low_stock_hysteresis_pct")}
					>
						{(control) => (
							<NumberInput
								{...control}
								integerOnly
								unit="%"
								className="max-w-40"
								disabled={disabled("low_stock_hysteresis_pct")}
								value={drafts.low_stock_hysteresis_pct}
								onChange={(event) => set("low_stock_hysteresis_pct")(event.target.value)}
							/>
						)}
					</Field>
					{caption("low_stock_hysteresis_pct")}
				</div>
			)}
			{trickle !== undefined && (
				<div className="flex flex-col gap-1">
					<div className="flex items-center justify-between gap-3">
						<span className="text-[13px] text-fg">{t("alerts.trickle")}</span>
						<Switch
							aria-label={t("alerts.trickle")}
							checked={trickle.valueJson === "true"}
							disabled={disabled("low_stock_realtime_trickle")}
							onCheckedChange={(next) =>
								mutation.mutate([["low_stock_realtime_trickle", next ? "true" : "false"]])
							}
						/>
					</div>
					{caption("low_stock_realtime_trickle")}
					{errorOf("low_stock_realtime_trickle") !== undefined && (
						<p role="alert" className="text-[12.5px] text-danger-fg">
							{errorOf("low_stock_realtime_trickle")}
						</p>
					)}
				</div>
			)}
			{mutation.isError && refusal === null && <ErrorState t={t} error={mutation.error} compact />}
			{editable && (
				<div>
					<Button type="submit" size="sm" disabled={busy || changed.length === 0}>
						{t("alerts.save")}
					</Button>
				</div>
			)}
		</form>
	);
}

function DigestPreviewPanel({
	t,
	preview,
}: {
	t: Translate;
	preview: () => Promise<DigestPreview>;
}) {
	const run = useMutation({ mutationFn: preview });
	return (
		<div className="flex flex-col gap-2 border-t border-border pt-3">
			<div>
				<Button
					type="button"
					size="sm"
					variant="secondary"
					disabled={run.isPending}
					onClick={() => run.mutate()}
				>
					{t("alerts.preview")}
				</Button>
			</div>
			{run.isError && <ErrorState t={t} error={run.error} compact />}
			{run.data !== undefined &&
				(run.data.categories.length === 0 ? (
					<p className="text-[12.5px] text-fg-muted">{t("alerts.previewEmpty")}</p>
				) : (
					<div className="flex flex-col gap-2" aria-live="polite">
						{run.data.categories.map((c) => (
							<div key={c.category}>
								<div className="text-[13px] font-semibold text-fg">
									{t(`preferences.notifications.categories.${c.category}`)}
								</div>
								<ul className="list-disc ps-5 text-[12.5px] text-fg-muted">
									{c.items.map((item) => (
										<li key={item} dir="auto">
											{item}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				))}
		</div>
	);
}
